/**
 * Game Center integration manager.
 *
 * Bridges JavaScript to the native GameCenterHandler in ViewController.swift
 * via WKWebView message handlers. All functions gracefully no-op when the
 * native bridge is unavailable (browser, Android, unauthenticated).
 */

import { GameStatistics } from '../Models/Statistics'

// ─── Constants ──────────────────────────────────────────────────────────────

const LEADERBOARD_ID = 'best_computed_time'

/** Achievement definitions: id → evaluation function returning percentComplete (0-100), or 0 to skip. */
const ACHIEVEMENTS: {
    id: string
    evaluate: (stats: GameStatistics, drawCount: number, undoCount: number, rawTimeMs: number) => number
}[] = [
    // Win milestones (incremental — report progress %)
    { id: 'first_win', evaluate: (s) => (s.gamesWon >= 1 ? 100 : 0) },
    { id: 'wins_10', evaluate: (s) => Math.min(100, (s.gamesWon / 10) * 100) },
    { id: 'wins_50', evaluate: (s) => Math.min(100, (s.gamesWon / 50) * 100) },
    { id: 'wins_100', evaluate: (s) => Math.min(100, (s.gamesWon / 100) * 100) },

    // Streak milestones
    { id: 'streak_3', evaluate: (s) => (s.currentStreak >= 3 ? 100 : 0) },
    { id: 'streak_5', evaluate: (s) => (s.currentStreak >= 5 ? 100 : 0) },
    { id: 'streak_10', evaluate: (s) => (s.currentStreak >= 10 ? 100 : 0) },

    // Special conditions (this game only)
    { id: 'speed_demon', evaluate: (_s, _d, _u, rawTime) => (rawTime < 180_000 ? 100 : 0) },
    { id: 'draw3_victor', evaluate: (_s, drawCount) => (drawCount === 3 ? 100 : 0) },
    { id: 'perfect_game', evaluate: (_s, _d, undoCount) => (undoCount === 0 ? 100 : 0) },
]

// ─── Module state ───────────────────────────────────────────────────────────

let _isAuthenticated = false

// ─── Native bridge helpers ──────────────────────────────────────────────────

/** Returns the native Game Center message handler, or undefined if unavailable. */
function getNativeBridge(): any | undefined {
    try {
        const wk = (window as any).webkit
        return wk?.messageHandlers?.gameCenter
    } catch {
        return undefined
    }
}

/** Returns true when running on iOS with the Game Center native bridge available. */
export function isGameCenterAvailable(): boolean {
    return !!getNativeBridge()
}

/** Post a message to the native Game Center handler. */
function postToNative(message: Record<string, unknown>): void {
    const bridge = getNativeBridge()
    if (bridge) {
        bridge.postMessage(message)
    }
}

// ─── Public API ─────────────────────────────────────────────────────────────

/**
 * Initialize Game Center authentication.
 * Call once on app boot. Sets up the JS callback and requests authentication
 * from the native side. No-op in browser / Android.
 */
export function initGameCenter(): void {
    if (!getNativeBridge()) {
        return
    }

    // Set up callback for native → JS auth result
    ;(window as any).__onGameCenterAuth = (result: { authenticated: boolean }) => {
        _isAuthenticated = result.authenticated
        if (_isAuthenticated) {
            console.log('[GameCenter] Authenticated')
        } else {
            console.log('[GameCenter] Not authenticated')
        }
    }

    postToNative({ action: 'authenticate' })
}

/**
 * Submit a score to the Game Center leaderboard.
 * @param computedTimeMs — Computed time in milliseconds (rawTime + undo penalty)
 */
export function submitGameCenterScore(computedTimeMs: number): void {
    if (!_isAuthenticated) return

    postToNative({
        action: 'submitScore',
        leaderboardId: LEADERBOARD_ID,
        score: Math.round(computedTimeMs),
    })
}

/**
 * Evaluate and report Game Center achievements based on current stats.
 * Should be called after recordGameWin() so stats reflect the current win.
 *
 * @param stats — Current game statistics (post-win)
 * @param drawCount — Draw count setting for this game (1 or 3)
 * @param undoCount — Number of undos used in this game
 * @param rawTimeMs — Raw completion time in milliseconds (no penalty)
 */
export function reportGameCenterAchievements(
    stats: GameStatistics,
    drawCount: number,
    undoCount: number,
    rawTimeMs: number
): void {
    if (!_isAuthenticated) return

    for (const achievement of ACHIEVEMENTS) {
        const percent = achievement.evaluate(stats, drawCount, undoCount, rawTimeMs)
        if (percent > 0) {
            postToNative({
                action: 'reportAchievement',
                achievementId: achievement.id,
                percentComplete: percent,
            })
        }
    }
}

/**
 * Open the Game Center dashboard (leaderboards, achievements, profile).
 * Triggered by the "Game Center" button in the menu.
 */
export function showGameCenterDashboard(): void {
    if (!_isAuthenticated) return

    postToNative({ action: 'showDashboard' })
}
